import type { TFunction } from 'i18next';
import type { ActivityDto } from '../types';

/** `ActivityDto.metadata` is an opaque JSON string — same convention as `TaskApiParams.filters`/`.sort`. */
export function parseActivityMetadata(raw: string | null): Record<string, unknown> {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

export interface ActivityMessage {
  actionText: string;
  detail?: string;
}

function oldToNew(
  t: TFunction,
  oldValue: string | null,
  newValue: string | null,
): string | undefined {
  if (oldValue != null && newValue != null)
    return t('activity.oldToNew', { old: oldValue, new: newValue });
  return newValue ?? oldValue ?? undefined;
}

function translatePriority(t: TFunction, value: string | null): string | null {
  if (!value) return value;
  const translated = t(`priority.${value.toLowerCase()}`);
  return translated === `priority.${value.toLowerCase()}` ? value : translated;
}

function translateUpdatedField(t: TFunction, field: string | null): string {
  if (!field) return t('activity.fields.default');
  const key = `activity.fields.${field}`;
  const translated = t(key);
  return translated === key ? field : translated;
}

/**
 * Builds the actor-independent part of an activity feed row's message — the caller prefixes it
 * with the actor's name. Falls back to a generic message (plus a raw old→new detail line, same
 * as before this mapping existed) for any `actionType` not in `TaskActivityActionType`, so an
 * unrecognized/future backend value degrades gracefully instead of rendering nothing useful.
 */
export function buildActivityMessage(activity: ActivityDto, t: TFunction): ActivityMessage {
  const { actionType, targetName, oldValue, newValue } = activity;
  const metadata = parseActivityMetadata(activity.metadata);

  switch (actionType) {
    case 'task_created': {
      let actionText = t('activity.actions.task_created');
      if (metadata.source === 'bulk_import') {
        actionText += ` ${t('activity.actions.task_created_bulkImportSuffix')}`;
      } else if (typeof metadata.copiedFromTaskId === 'string') {
        actionText += ` ${t('activity.actions.task_created_copiedSuffix')}`;
      }
      return { actionText };
    }

    case 'task_updated':
      return {
        actionText: t('activity.actions.task_updated', {
          field: translateUpdatedField(t, targetName),
        }),
        detail: oldToNew(t, oldValue, newValue),
      };

    case 'task_moved':
      return {
        actionText: t('activity.actions.task_moved'),
        detail: oldToNew(t, oldValue, newValue),
      };

    case 'task_group_changed':
      return {
        actionText: t('activity.actions.task_group_changed'),
        detail: oldToNew(t, oldValue ?? t('activity.noGroup'), newValue ?? t('activity.noGroup')),
      };

    case 'task_assigned':
      return {
        actionText: t('activity.actions.task_assigned'),
        detail: oldToNew(
          t,
          oldValue ?? t('metadata.noAssignee'),
          newValue ?? t('metadata.noAssignee'),
        ),
      };

    case 'task_priority_changed':
      return {
        actionText: t('activity.actions.task_priority_changed'),
        detail: oldToNew(t, translatePriority(t, oldValue), translatePriority(t, newValue)),
      };

    case 'task_toggled':
      return {
        actionText:
          newValue === 'True'
            ? t('activity.actions.task_toggled_done')
            : t('activity.actions.task_toggled_undone'),
      };

    case 'task_blocked_reason_changed':
      return {
        actionText:
          newValue != null
            ? t('activity.actions.task_blocked_reason_changed_set')
            : t('activity.actions.task_blocked_reason_changed_cleared'),
        detail: newValue ?? undefined,
      };

    case 'task_archived':
      return { actionText: t('activity.actions.task_archived') };

    case 'task_unarchived':
      return { actionText: t('activity.actions.task_unarchived') };

    case 'subtask_added':
      return { actionText: t('activity.actions.subtask_added'), detail: newValue ?? undefined };

    case 'task_dependency_added':
      return {
        actionText: t('activity.actions.task_dependency_added'),
        detail: newValue ?? undefined,
      };

    case 'task_dependency_removed':
      return {
        actionText: t('activity.actions.task_dependency_removed'),
        detail: oldValue ?? undefined,
      };

    case 'tag_added':
      return {
        actionText: t('activity.actions.tag_added'),
        detail: targetName ?? undefined,
      };

    case 'tag_removed':
      return {
        actionText: t('activity.actions.tag_removed'),
        detail: targetName ?? undefined,
      };

    case 'attachment_uploaded':
      return {
        actionText: t('activity.actions.attachment_uploaded'),
        detail: targetName ?? undefined,
      };

    case 'attachment_deleted':
      return {
        actionText: t('activity.actions.attachment_deleted'),
        detail: targetName ?? undefined,
      };

    case 'field_value_upserted':
      return {
        actionText: t('activity.actions.field_value_upserted', {
          field: targetName ?? t('activity.fields.default'),
        }),
        detail: oldToNew(t, oldValue, newValue),
      };

    case 'checklist_created':
      return {
        actionText: t('activity.actions.checklist_created'),
        detail: newValue ?? targetName ?? undefined,
      };

    case 'checklist_renamed':
      return {
        actionText: t('activity.actions.checklist_renamed'),
        detail: oldToNew(t, oldValue, newValue),
      };

    case 'checklist_deleted':
      return {
        actionText: t('activity.actions.checklist_deleted'),
        detail: oldValue ?? targetName ?? undefined,
      };

    case 'checklist_item_added':
      return {
        actionText: t('activity.actions.checklist_item_added', { checklist: targetName ?? '' }),
        detail: newValue ?? undefined,
      };

    case 'checklist_item_updated':
      return {
        actionText: t('activity.actions.checklist_item_updated', { checklist: targetName ?? '' }),
        detail: oldToNew(t, oldValue, newValue),
      };

    case 'checklist_item_toggled':
      return {
        actionText:
          newValue === 'True'
            ? t('activity.actions.checklist_item_toggled_done', { item: targetName ?? '' })
            : t('activity.actions.checklist_item_toggled_undone', { item: targetName ?? '' }),
      };

    case 'checklist_item_deleted':
      return {
        actionText: t('activity.actions.checklist_item_deleted', { checklist: targetName ?? '' }),
        detail: oldValue ?? undefined,
      };

    case 'checklist_item_moved':
      return {
        actionText: t('activity.actions.checklist_item_moved', { item: targetName ?? '' }),
        detail: oldToNew(t, oldValue, newValue),
      };

    case 'checklist_item_converted':
      return {
        actionText:
          metadata.convertedTo === 'subtask'
            ? t('activity.actions.checklist_item_converted_subtask', { item: oldValue ?? '' })
            : t('activity.actions.checklist_item_converted_task', { item: oldValue ?? '' }),
        detail: targetName ?? undefined,
      };

    case 'comment_added':
      return {
        actionText: t('activity.actions.comment_added'),
        detail: newValue ?? undefined,
      };

    case 'comment_deleted':
      return {
        actionText: t('activity.actions.comment_deleted'),
        detail: oldValue ?? undefined,
      };

    default:
      return { actionText: t('activity.actions.default'), detail: oldToNew(t, oldValue, newValue) };
  }
}
